"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { LiquidGlassCard } from "@/components/ui/liquid-glass-card";
import {
  Briefcase,
  Calendar,
  MapPin,
  Building2,
  Laptop,
  CheckCircle2,
  Circle,
  Sparkles,
} from "lucide-react";

const experiences = [
  {
    company: "Riseup Labs",
    location: "Dhaka, Bangladesh",
    position: "Software Engineer",
    type: "Full Time",
    workMode: "Onsite",
    period: "Oct 2025 – Present",
    isCurrent: true,
    logoUrl: "/work/riseuplabs_logo.jpeg",
    responsibilities: [
      "Develop production-grade business applications for Akij Venture Ltd. through frontend features, backend APIs, and PostgreSQL workflows.",
      "Build secure REST APIs with Nest.js using DTO validation, middleware, modular services, and production business logic.",
      "Optimize PostgreSQL schemas and ORM queries, improving query performance by 35% through indexing and relational modeling.",
      "Improve frontend performance by 30% through reusable components, efficient rendering, and optimized API integration flows.",
      "Collaborate with product, QA, design, and engineering teams to ship maintainable production-ready features in Agile sprints.",
    ],
  },
  {
    company: "StrategyByte",
    location: "Remote, Australia",
    position: "Software Developer",
    type: "Full Time",
    workMode: "Remote",
    period: "Jul 2024 – Sep 2025",
    isCurrent: false,
    logoUrl: "/work/strategybyte_logo.jpeg",
    responsibilities: [
      "Developed a full-stack appointment booking platform using Nest.js, TypeORM, PostgreSQL, React, Next.js, and TypeScript.",
      "Built backend modules for scheduling, conflict handling, payments, calendar sync, and admin operations.",
      "Integrated Stripe and Stripe Connect for secure payments, platform transactions, and automated payouts.",
      "Implemented Google Calendar API sync to reduce double-booking risk.",
      "Automated CI/CD with GitHub Actions and deployed to DigitalOcean and VPS/Linux environments.",
      "Containerized workflows with Docker to improve development, testing, and deployment consistency.",
    ],
  },
  {
    company: "Repliq Limited",
    location: "Dhaka, Bangladesh",
    position: "Jr. Software Developer",
    type: "Full Time",
    workMode: "Onsite",
    period: "Jul 2023 – Jun 2024",
    isCurrent: false,
    logoUrl: "/work/repliqltd_logo.jpeg",
    responsibilities: [
      "Developed full-stack modules for a B2B healthcare platform, including responsive frontend interfaces and backend API integrations.",
      "Built reusable frontend components with React and TypeScript using modern UI libraries.",
      "Improved API integrations and resolved production-level UI/UX issues to enhance stability and user experience.",
      "Debugged frontend and backend issues, tested APIs with Postman, and supported production releases in Agile sprints.",
    ],
  },
  {
    company: "AlgoSoft Technologies Limited",
    location: "Remote",
    position: "React Developer Intern",
    type: "Internship",
    workMode: "Remote",
    period: "Jan 2023 – Jun 2023",
    isCurrent: false,
    logoUrl: "/work/algosoftbd_logo.jpeg",
    responsibilities: [
      "Built reusable UI component libraries using React, Ant Design, and Storybook for dashboards and internal tools.",
      "Translated design requirements into responsive, maintainable React components with consistent layout and styling.",
    ],
  },
];

import { usePortfolio } from "@/components/portfolio-provider";

export default function Experience() {
  const { data } = usePortfolio();
  const dbExperiences =
    data?.experiences && data.experiences.length > 0 ? data.experiences : null;

  const displayExperiences = dbExperiences
    ? dbExperiences.map((exp) => ({
        company: exp.company,
        location: exp.location,
        position: exp.position,
        type: exp.employmentType,
        workMode: exp.workMode,
        period: exp.period,
        isCurrent: exp.isCurrent,
        responsibilities: exp.responsibilities,
        logoUrl: exp.logoUrl,
      }))
    : experiences;

  return (
    <section id="experience" className="section-shell relative overflow-x-clip py-10 sm:py-14 md:py-18 lg:py-20">
      {/* Background Liquid Ambient Light */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -right-24 top-1/4 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.12)_0%,transparent_70%)]" />
        <div className="absolute -left-20 bottom-1/3 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.08)_0%,transparent_70%)]" />
      </div>

      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-1.5 backdrop-blur-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              Career Path
            </span>
          </div>
          <h2 className="section-title mb-4 font-bold">
            Work <span className="accent-gradient-text">Experience</span>
          </h2>
          <p className="section-subtitle mx-auto max-w-2xl text-muted-foreground">
            A track record of engineering scalable platforms, optimizing critical
            data layers, and driving user and revenue growth.
          </p>
        </motion.div>

        {/* Apple Conduit Timeline */}
        <div className="relative">
          {/* Luminous Central Light Conduit */}
          <div className="absolute bottom-0 top-0 hidden w-0.5 -translate-x-1/2 bg-gradient-to-b from-primary via-primary/50 to-transparent md:left-1/2 md:block shadow-[0_0_12px_rgba(201,168,76,0.5)]" />

          <div className="space-y-8 md:space-y-14">
            {displayExperiences.map((exp, index) => {
              const isEven = index % 2 === 0;
              return (
                <motion.div
                  key={exp.company}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative flex flex-col md:flex-row gap-6 md:gap-16 items-center ${
                    isEven ? "md:flex-row-reverse" : ""
                  }`}
                >
                  {/* Central Node Sphere */}
                  <div className="hidden md:block absolute md:left-1/2 -translate-x-1/2 z-20">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full border backdrop-blur-2xl transition-transform duration-300 shadow-[0_0_20px_rgba(0,0,0,0.8)] ${
                        exp.isCurrent
                          ? "border-primary/80 bg-primary/20 shadow-[0_0_25px_rgba(201,168,76,0.6),inset_0_1px_1px_rgba(255,255,255,0.4)]"
                          : "border-white/20 bg-[#16110a]/90 shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]"
                      }`}
                    >
                      {exp.isCurrent ? (
                        <span className="relative flex h-3 w-3">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                          <span className="relative inline-flex h-3 w-3 rounded-full bg-primary shadow-[0_0_8px_rgba(201,168,76,0.9)]" />
                        </span>
                      ) : (
                        <CheckCircle2 className="h-4 w-4 text-primary/80" />
                      )}
                    </div>
                  </div>

                  {/* Spacer for 50/50 layout */}
                  <div className="hidden md:block flex-1" />

                  {/* Experience Card */}
                  <div className="w-full flex-1">
                    <LiquidGlassCard
                      className={`p-6 sm:p-7 rounded-3xl ${
                        exp.isCurrent
                          ? "border-primary/35 shadow-[0_20px_50px_rgba(201,168,76,0.1)]"
                          : ""
                      }`}
                      spotlightColor="rgba(201, 168, 76, 0.2)"
                    >
                      {/* Current Status Pill Tag */}
                      {exp.isCurrent && (
                        <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-xs font-semibold text-primary backdrop-blur-md">
                          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                          <span>Current Role</span>
                        </div>
                      )}

                      {/* Header Area */}
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5">
                        <div className="flex items-start gap-3.5">
                          {/* Company Logo Badge */}
                          <div className="relative h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-2xl border border-white/15 bg-white/[0.04] p-1.5 backdrop-blur-xl shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_1px_rgba(255,255,255,0.15)] flex items-center justify-center overflow-hidden">
                            {exp.logoUrl ? (
                              <Image
                                src={exp.logoUrl}
                                alt={`${exp.company} logo`}
                                width={56}
                                height={56}
                                className="h-full w-full object-cover rounded-xl"
                              />
                            ) : (
                              <Building2 className="w-6 h-6 text-primary/70" />
                            )}
                          </div>

                          <div className="space-y-1">
                            <h3 className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
                              <span>{exp.position}</span>
                            </h3>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <span className="font-semibold text-primary">
                                {exp.company}
                              </span>
                              <span className="text-muted-foreground">•</span>
                              <span className="text-muted-foreground flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {exp.location}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Metadata Pills */}
                        <div className="flex flex-wrap sm:flex-col sm:items-end gap-1.5">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                            <Calendar className="w-3.5 h-3.5 text-primary/70" />
                            <span>{exp.period}</span>
                          </div>
                          <div className="flex gap-1.5">
                            <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-0.5 text-[11px] font-semibold text-foreground/80">
                              {exp.type}
                            </span>
                            <span className="rounded-full border border-primary/25 bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary flex items-center gap-1">
                              {exp.workMode === "Remote" ? (
                                <Laptop className="w-3 h-3" />
                              ) : (
                                <Building2 className="w-3 h-3" />
                              )}
                              {exp.workMode}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Responsibilities */}
                      <div className="space-y-3 pt-1 border-t border-white/10">
                        {exp.responsibilities.map((resp, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-3 text-muted-foreground text-sm leading-relaxed group/item"
                          >
                            <div className="shrink-0 w-4 h-4 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center mt-1 group-hover/item:bg-primary/30 transition-colors">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                            </div>
                            <p className="flex-1 text-foreground/85">{resp}</p>
                          </div>
                        ))}
                      </div>
                    </LiquidGlassCard>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
